import moment from 'moment';
import uuidV4 from 'uuid/v4';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import WapiService from 'wanderu-service-wapi';

import {
    setTripCheckoutData,
    setTripLiveCheck,
    removeTripLiveChecks,
} from '../../appstate/actions';
import {
    selectLiveCheckStatusData,
} from '../../appstate/selectors';
import { LIVECHECK_TTL_IN_MS } from '../../constants';
import {
    serializeLiveCheckTripParams as serializeParams,
    buildLiveLinkBody,
} from '../utils';
import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';
import Events from './events/liveCheckTripEvents';

const logger = Logger.getLogger('wanderu-ducks-live-check:liveCheckTrip');

async function performLiveCheck(opts, signal) {
    const body = buildLiveLinkBody(opts);
    const livecheckId = uuidV4();
    body.livecheck_id = livecheckId;
    return WapiService.post('live/link.json', {
        body,
        signal,
    });
}

export function liveCheckTrip(tripId, opts, signal) {
    return async (dispatch, getState) => {
        const {
            autoEnablePromos = true,
            autofill = false,
            seats = 1,
            wheelchairs = 0,
            selectedFare,
            tripIds: allTripIds,
            currency,
            exchangeRate,
            locale,
            affiliateId,
            trackingId,
            eventMeta,
            priceAcknowledged,
            fareVersion,
            tripSource,
        } = opts;

        const lcParams = {
            tripId,
            seats,
            wheelchairs,
            selectedFare,
        };

        const working = selectors.selectWorking(getState(), lcParams);
        const success = selectors.selectSuccess(getState(), lcParams);
        if (working || success) {
            return;
        }

        let errorObj;
        dispatch(actions.startWork(lcParams));

        let meta = {
            expiresAt: moment().add(LIVECHECK_TTL_IN_MS).valueOf(),
        };

        const lcId = serializeParams(lcParams);

        try {
            const response = await performLiveCheck({
                allTripIds,
                autoEnablePromos,
                autofill,
                currency,
                exchangeRate,
                locale,
                seats,
                tripId,
                wheelchairs,
                selectedFare,
                affiliateId,
                trackingId,
                fareVersion,
            }, signal);

            const {
                error,
                link,
                nativeBookingEnabled,
                op,
                roundTripEnabled,
            } = response.result;
            const {
                args,
                data,
                deepLinkToken,
                message,
            } = op;

            if (error) {
                meta = {
                    expiresAt: undefined,
                };
                return;
            }

            dispatch(setTripLiveCheck(lcId, {
                nativeBookingEnabled,
                timestamp: Date.now(),
                message,
                tripData: data,
                liveCheckArgs: args,
                priceAcknowledged,
            }));

            if (!link) {
                return;
            }

            dispatch(setTripCheckoutData(lcId, {
                link,
                nativeBookingEnabled,
                roundTripEnabled,
                token: deepLinkToken,
                selectedFare,
            }));
        } catch (ex) {
            meta = {
                expiresAt: undefined,
            };

            if (ex.name !== 'AbortError') {
                const domainUserInfo = appSettings.env.isClient
                    ? SnowplowService.getDomainUserInfo()
                    : {};

                logger.error('liveCheck', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                });
            }
            errorObj = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };

            if (ex.code === 'INVALID_LIVE_SERVICE_QUERY_EXPIRED_TRIP') {
                dispatch(removeTripLiveChecks(lcId));
            }
        } finally {
            const {
                itineraryId,
                lastScraped,
                originalPricing,
            } = eventMeta;

            const liveCheckStatusData = selectLiveCheckStatusData(
                getState(),
                lcParams,
                originalPricing,
            );

            if (liveCheckStatusData) {
                Events.tripLivecheckCompleted({
                    label: liveCheckStatusData.status,
                    tripId,
                    itineraryId,
                    lastScraped,
                    price: liveCheckStatusData.price,
                    priceDifference: liveCheckStatusData.priceDifference,
                    selectedFare,
                    tripSource,
                });
            }

            dispatch(actions.endWork(lcParams, meta, errorObj));
        }
    };
}

const liveCheckTripAction = {
    liveCheckTrip,
    reset: actions.reset,
    ...selectors,
};

export {
    liveCheckTripAction,
};

export default {
    [namespace]: reducer,
};
